<template>
    <v-card>
        <v-card-title primary-title>{{ dialogTitle }}</v-card-title>
        <v-card-text>
            <v-form ref="experimentMethodForm" v-model="isFormValid" @submit.prevent="saveExperimentMethod">
                <v-row>
                    <v-col cols="12">
                        <v-text-field label="Name" v-model="name" :rules="nameRequiredRule" />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-autocomplete
                            label="Platform"
                            v-model="platform"
                            :items="platformOptions"
                            :rules="platformRequiredRule"
                            clearable
                            :disabled="experimentMethod != null"
                        />
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="cancel"> Cancel </v-btn>
            <v-btn color="primary" @click="saveExperimentMethod" :disabled="!isFormValid"> Save </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { withLoadingScreen } from "@/lib/loading";
import { experimentMethodsAPI, SavedExperimentMethod } from "@/services/experimentMethods/experimentMethodsAPI";
import { DropdownListItem } from "@/lib/ts/dropdownListItemUtils";
import { PlatformDTO } from "@/services/commonModels";
import { experimentsAPI, mapPlatformsToDropdownItems } from "@/services/experiments/experimentsAPI";
import { requiredSimpleValueRules } from "@/lib/validationUtils";

const { experimentMethod } = defineProps<{
    experimentMethod?: SavedExperimentMethod;
}>();

const emit = defineEmits<{
    (e: "cancelForm"): void;
    (e: "experimentMethodSaved", savedExperimentMethod: SavedExperimentMethod);
}>();

const name = ref(experimentMethod ? experimentMethod.name : "");
const platform = ref(experimentMethod ? experimentMethod.platform : null);
const platformOptions = ref<DropdownListItem<PlatformDTO>[]>([]);

const nameRequiredRule = requiredSimpleValueRules("Name");
const platformRequiredRule = requiredSimpleValueRules("Platform");
const isFormValid = ref(false);

async function populateDropdownOptions() {
    await withLoadingScreen(async () => {
        const platforms = await experimentsAPI.fetchPlatforms();
        platformOptions.value = mapPlatformsToDropdownItems(platforms);
    });
}


const dialogTitle = computed(() => {
    return experimentMethod ? "Edit Experiment Method" : "Create Experiment Method";
});

function cancel() {
    emit("cancelForm");
}

async function saveExperimentMethod() {
    await withLoadingScreen(async () => {
        let savedExperimentMethod: SavedExperimentMethod;
        if (experimentMethod) {
            savedExperimentMethod = await updateExperimentMethod();
        } else {
            savedExperimentMethod = await createExperimentMethod();
        }
        emit("experimentMethodSaved", savedExperimentMethod);
    });
}

async function createExperimentMethod(): Promise<SavedExperimentMethod> {
    const unsavedExperimentMethod = {
        name: name.value,
        platform: platform.value!
    };
    return await experimentMethodsAPI.createExperimentMethod(unsavedExperimentMethod);
}

async function updateExperimentMethod(): Promise<SavedExperimentMethod> {
    const experimentMethodToUpdate: SavedExperimentMethod = {
        ...experimentMethod!,
        name: name.value,
        platform: platform.value!
    };
    console.log(experimentMethodToUpdate);
    return await experimentMethodsAPI.updateExperimentMethod(experimentMethodToUpdate);
}

populateDropdownOptions();
</script>
